import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, setSeo, track } from "@/lib/api";
import { GuideCard } from "@/components/site/Blocks";
import { CommercialModule } from "@/components/site/CommercialModule";
import { Loading } from "@/pages/NotFound";
import { SmartImage } from "@/components/site/SmartImage";

const SLUG = "comfort-sleep";

// Offer categories exist so approved sleep inventory can be attached per product group later.
// Until an approved offer exists for a category, the module renders the site's disabled CTA.
const OPTION_GROUPS = [
  { title: "Adjustable Bases", category: "adjustable-bases", note: "Bases that raise and lower the head and foot sections." },
  { title: "Split Systems", category: "split-systems", note: "Two independently controlled sides for two sleepers." },
  { title: "Mattresses", category: "adjustable-bed-mattresses", note: "Mattresses built to flex with an adjustable base." },
  { title: "Smart Sleep", category: "smart-sleep", note: "App controls, saved positions and connected features." },
  { title: "Temperature Control", category: "temperature-controlled-sleep", note: "Systems that cool, warm or zone the sleep surface." },
];

const FEATURES = [
  ["Head & foot adjustment", "Changes the angle of the upper and lower sections of an adjustable bed."],
  ["Zero gravity", "A preset position available on many adjustable bases that raises both the upper and lower body. Exact positioning varies by manufacturer."],
  ["Split adjustment", "Allows each side of a compatible split sleep system to move independently."],
  ["Massage", "Some adjustable bases include vibration or zoned massage with different intensity or program settings."],
  ["Anti-snore / head elevation", "Some bases include a preset that slightly elevates the head."],
  ["Lumbar support", "Available on some adjustable bases and designed to provide additional adjustable support around the lower-back area."],
  ["Smart controls", "Depending on the product, this can include apps, remote controls, saved positions, voice controls, automation or other connected features."],
  ["Convenience features", "Some bases include features such as under-bed lighting, USB charging or programmable presets."],
];

const BEFORE_YOU_BUY = [
  ["Mattress compatibility", "Confirm that the mattress is designed or approved for use with an adjustable base. Not every foam, latex or hybrid mattress is automatically compatible."],
  ["Bed-frame compatibility", "Some adjustable bases can work inside existing bed frames or with existing headboards, but dimensions, clearance and installation requirements vary."],
  ["Weight capacity", "Check the manufacturer's published capacity and understand whether the stated limit includes sleepers, the mattress or other loads."],
  ["Setup & warranty", "Adjustable bases can be heavy and installation requirements vary. Warranty coverage may also differ between structural, mechanical and electrical components."],
];

// The two launch guides are the prominent reading at the bottom. Other published comfort guides stay
// available through search and the sitemap without defining the category.
const LAUNCH_GUIDES = [
  "split-king-vs-king-adjustable-bed-whats-the-difference",
  "can-you-use-any-mattress-on-an-adjustable-bed",
];

export default function ComfortPage() {
  const [hub, setHub] = useState(null);

  useEffect(() => {
    api.get(`/hubs/${SLUG}`).then((r) => {
      setHub(r.data);
      setSeo({
        title: r.data.meta_title ||
          "Adjustable Beds & Sleep Systems — Comfort & Sleep — Human Home Living",
        description: r.data.meta_description ||
          "Adjustable beds, split sleep systems, compatible mattresses, smart beds and temperature-control systems. Understand what each option does, then explore products that fit your needs.",
      });
      track({ event_type: "page_view", page: `/hub/${SLUG}` });
    }).catch(() => {});
  }, []);

  if (!hub) return <Loading label="Loading comfort & sleep" />;

  const guides = (hub.articles || []).filter((a) => LAUNCH_GUIDES.includes(a.slug));

  return (
    <div data-testid="hub-page-comfort-sleep">
      <section className="relative h-[46vh] min-h-[340px]">
        <SmartImage
          src={hub.image}
          alt="A calm bedroom in soft morning light"
          priority
          sizes="100vw"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/88 via-black/62 to-black/45" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/55 to-transparent" />
        <div className="relative h-full max-w-[1280px] mx-auto px-5 md:px-8 flex flex-col justify-end pb-12 text-white">
          <nav className="overlay-copy-strong text-[14px] font-semibold text-white mb-5">
            <Link to="/" className="hover:text-white">
              Home
            </Link>
            <span className="mx-2">/</span>
            <span>Comfort &amp; Sleep</span>
          </nav>
          <span className="eyebrow eyebrow-hero mb-3">Comfort &amp; Sleep</span>
          <h1 className="overlay-title text-[40px] md:text-[58px] leading-[1.03] max-w-3xl">
            Sleep products that fit the way you actually sleep.
          </h1>
          <p className="overlay-copy-strong text-[19px] md:text-[24px] font-medium mt-4 text-white max-w-2xl">
            Adjustable beds, split sleep systems, compatible mattresses, smart beds and
            temperature-control systems give you more ways to personalize your sleep setup.
            Understand what each option does, then explore products that fit your needs.
          </p>
        </div>
      </section>

      <section className="px-5 md:px-8 py-14 md:py-20">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-3xl">
            <span className="eyebrow">About this category</span>
            <p className="text-[18px] md:text-[22px] font-normal leading-[1.55] text-[#3F3F3F] mt-5">
              {hub.intro}
            </p>
          </div>

          <div className="mt-12 md:mt-16" data-testid="comfort-product-types">
            <span className="eyebrow">Explore sleep options</span>
            <div className="grid md:grid-cols-3 gap-px bg-line mt-5">
              {(hub.money_paths || []).map((m) => (
                <div
                  key={m.name}
                  className="bg-white px-6 py-7"
                  data-testid={`product-type-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <h3 className="text-[17.5px] font-medium text-charcoal">{m.name}</h3>
                  <p className="text-[16px] md:text-[17px] text-[#454545] mt-2 leading-relaxed">{m.note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F6F4F0] border-y border-line px-5 md:px-8 py-14 md:py-20" data-testid="comfort-shopping">
        <div className="max-w-[1280px] mx-auto">
          <span className="eyebrow">Explore your options</span>
          <h2 className="display text-[32px] md:text-[42px] leading-tight text-charcoal mt-4 max-w-2xl">
            Sleep options, organised by product group
          </h2>
          <p className="text-[17px] md:text-[18px] font-light leading-[1.6] text-[#454545] mt-4 max-w-2xl">
            Go straight to the part of the setup you are shopping for. There is no questionnaire to
            complete first.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-line mt-9">
            {OPTION_GROUPS.map((g) => (
              <div
                key={g.category}
                className="bg-white px-6 py-7 flex flex-col"
                data-testid={`option-group-${g.category}`}
              >
                <h3 className="text-[17.5px] font-medium text-charcoal">{g.title}</h3>
                <p className="text-[16px] text-[#454545] mt-2 leading-relaxed flex-1">{g.note}</p>
                <div className="mt-5">
                  <CommercialModule
                    category={g.category}
                    fallback="sleep-systems"
                    label="View options"
                    compact
                    placement="EARLY CTA"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 md:px-8 py-14 md:py-20" data-testid="comfort-features">
        <div className="max-w-[1280px] mx-auto">
          <span className="eyebrow">Understand the features</span>
          <h2 className="display text-[30px] md:text-[40px] leading-tight text-charcoal mt-4 max-w-2xl">
            What the common features actually do
          </h2>
          <div className="grid md:grid-cols-2 gap-px bg-line mt-9">
            {FEATURES.map(([name, note]) => (
              <div key={name} className="bg-white px-6 py-7">
                <h3 className="text-[17.5px] font-medium text-charcoal">{name}</h3>
                <p className="text-[16px] md:text-[17px] text-[#454545] mt-2 leading-relaxed">{note}</p>
              </div>
            ))}
          </div>
          <p className="text-[14px] text-[#6E6A63] mt-5">
            Exact features vary by manufacturer and model.
          </p>
        </div>
      </section>

      <section className="bg-charcoal text-white px-5 md:px-8 py-16 md:py-20" data-testid="comfort-before-you-buy">
        <div className="max-w-[1280px] mx-auto">
          <span className="eyebrow eyebrow-light">Before you buy</span>
          <h2 className="display text-[30px] md:text-[40px] leading-tight mt-4 max-w-2xl">
            Four practical checks
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-9 mt-10">
            {BEFORE_YOU_BUY.map(([name, note]) => (
              <div key={name}>
                <h3 className="text-[17.5px] font-medium text-white">{name}</h3>
                <p className="text-[16px] md:text-[17px] text-[#CBC8C2] mt-2 leading-relaxed">{note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {guides.length > 0 && (
        <section className="px-5 md:px-8 py-14 md:py-20" data-testid="comfort-guides">
          <div className="max-w-[1280px] mx-auto">
            <span className="eyebrow">Learn more about adjustable beds &amp; sleep systems</span>
            <div
              data-testid="hub-articles"
              className="grid gap-6 lg:gap-7 mt-8 md:grid-cols-2 max-w-3xl"
            >
              {guides.map((a, i) => (
                <GuideCard key={a.slug} a={a} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="px-5 md:px-8 pb-8">
        <div className="max-w-[1080px] mx-auto">
          <CommercialModule
            category="adjustable-bases"
            fallback="sleep-systems"
            label="See Current Options"
            headline="Ready to look at sleep options?"
            note="Explore the options in this category whenever you're ready."
            placement="FINAL CTA"
          />
        </div>
      </section>
    </div>
  );
}
